import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { devTime, devTimeEnd, devLog, devWarn, logCriticalError } from '../lib/devLog';

interface DashboardStats {
  notesThisWeek: number;
  notesLastWeek: number;
  notesChange: number;
  notesTrend: 'up' | 'down' | 'stable';
  clientsSeenThisWeek: number;
  clientsSeenLastWeek: number;
  clientsChange: number;
  clientsTrend: 'up' | 'down' | 'stable';
  activeClients: number;
  inactiveClients: number;
  totalSessions: number;
  sessionsToday: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  sessionsThisYear: number;
  recentActivities: Array<{
    id: string;
    type: 'note' | 'client';
    title: string;
    subtitle: string;
    timestamp: string;
    link: string;
  }>;
  clientsNeedingFollowup: Array<{
    id: string;
    first_name: string;
    last_session: string;
  }>;
  clientMilestones: Array<{
    id: string;
    first_name: string;
    therapy_start_date: string;
    days_in_therapy: number;
    milestone: number;
    days_until_milestone: number;
  }>;
}

const CACHE_KEY = 'dashboard_stats_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedStats {
  data: DashboardStats;
  timestamp: number;
}

export function useDashboardStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async (forceRefresh = false) => {
    if (!user) return;

    devTime('[Dashboard] Total load time');
    devTime('[Dashboard] Cache check');

    // Check cache first
    if (!forceRefresh) {
      try {
        const cachedData = sessionStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const cached: CachedStats = JSON.parse(cachedData);
          const now = Date.now();
          if (now - cached.timestamp < CACHE_DURATION) {
            devTimeEnd('[Dashboard] Cache check');
            devLog('[Dashboard] ⚡ Using cached data');
            setStats(cached.data);
            setLoading(false);
            devTimeEnd('[Dashboard] Total load time');
            return;
          }
        }
      } catch (e) {
        // Ignore cache errors and fetch fresh data
      }
    }
    devTimeEnd('[Dashboard] Cache check');

    try {
      setLoading(true);
      setError(null);

      devTime('[Dashboard] RPC call');
      const { data, error: rpcError } = await supabase.rpc('get_dashboard_stats');
      devTimeEnd('[Dashboard] RPC call');

      if (rpcError) throw rpcError;

      const dashboardStats: DashboardStats = data as DashboardStats;

      // Cache the results
      devTime('[Dashboard] Cache write');
      try {
        const cacheData: CachedStats = {
          data: dashboardStats,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        devLog('[Dashboard] ✓ Stats cached for 5 minutes');
      } catch (e) {
        devWarn('[Dashboard] Failed to cache stats:', e);
      }
      devTimeEnd('[Dashboard] Cache write');

      setStats(dashboardStats);
      devTimeEnd('[Dashboard] Total load time');
      devLog('[Dashboard] ✓ Dashboard loaded successfully');
    } catch (err) {
      logCriticalError('[Dashboard] Error fetching dashboard stats', err);
      devTimeEnd('[Dashboard] Total load time');
      setError(err instanceof Error ? err.message : 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Defer dashboard stats loading to avoid competing with auth queries
    // Reduced from 1000ms to 400ms since access check now completes faster
    const timer = setTimeout(() => {
      fetchStats();
    }, 400);

    return () => clearTimeout(timer);
  }, [user]);

  return {
    stats,
    loading,
    error,
    refetch: () => fetchStats(true),
  };
}
